import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Field, Spinner } from "@cntrl/ui";
import { useAuth } from "../../app/auth/useAuth.js";
import { AuthError } from "../../app/auth/session.js";
import { useMe } from "../../app/hooks/useSession.js";
import {
  useAcceptInvite,
  useInvitePreview,
  type InvitePreview,
} from "../../app/hooks/useMyInvites.js";
import { AuthCard } from "./AuthCard.js";
import styles from "./AuthForm.module.scss";

const schema = z
  .object({
    firstName: z.string().trim().min(1, "Enter your first name").max(80, "Keep it to 80 characters"),
    lastName: z.string().trim().min(1, "Enter your last name").max(80, "Keep it to 80 characters"),
    password: z.string().min(12, "Use at least 12 characters"),
    confirm: z.string().min(1, "Confirm your password"),
  })
  .refine((v) => v.password === v.confirm, { message: "Passwords don't match", path: ["confirm"] });
type JoinValues = z.infer<typeof schema>;

export interface JoinScreenProps {
  /** The token from the invite link (`/join?token=…`). */
  token: string;
  /** Signed in and accepted — go to `/`, where the router picks the shell (band, roster, or the partner view). */
  onJoined: () => void;
  /** Send an existing account to sign in, coming back here afterwards. */
  onSignIn: () => void;
  /** Leave a DEAD-END invite (already used, invalid, an artist account) for plain sign-in — NOT coming back here: the
   *  invite cannot be accepted, so a return trip would land on this same card (and an already-signed-in visitor would
   *  bounce straight back, so the button looked like it did nothing). Signed in, sign-in forwards to the home page. */
  onLeave: () => void;
}

/** The heading and sentence for each invite kind. A partner invite is worded as what it is — following someone's
 *  schedule — never as joining anything. */
function describe(p: InvitePreview): { title: string; subtitle: string } {
  const who = p.inviterName || "Someone";
  switch (p.kind) {
    case "partner":
      return {
        title: `Keep up with ${who}`,
        subtitle: `${who} invited you to follow their shows, travel and guest list on Artist CNTRL.`,
      };
    case "org_seat":
      return {
        title: `Join ${p.orgName ?? "the team"}`,
        subtitle: `${who === p.orgName ? `${who} invited you to join their team` : `${who} invited you to join ${p.orgName ?? "their team"}`}${p.titleLabel ? ` as ${p.titleLabel}` : ""}.`,
      };
    default:
      // An invite from the band's ARTIST account is the band itself (that account carries the band's name), so the
      // sentence says "their crew" rather than naming the band twice.
      return {
        title: `Join ${p.bandName ?? "the band"}`,
        subtitle: `${who === p.bandName ? `${who} invited you to join their crew` : `${who} invited you to join ${p.bandName ?? "the crew"}`}${p.titleLabel ? ` as ${p.titleLabel}` : ""}.`,
      };
  }
}

/**
 * Joining by invite (ART-239) — the destination of every seat invite's link: crew, org team, partner. Before it,
 * no invite could complete for anyone the fixture seeder had not already made.
 *
 * Four outcomes, read from the server's preview: the link is no longer valid · it was sent to an artist account ·
 * the address already has an account (sign in, then accept here) · or it does not, and this page creates one for
 * that address and accepts in a single step. Signed in already, it is one Accept button — or, if you are signed
 * in as somebody else, it says so rather than accepting for the wrong person.
 *
 * The address is shown, not typed: the account is made for the address the invite names, which is what every
 * accept gate matches on. It rides an `autoComplete="username"` field beside the `new-password` pair so a password
 * manager files the new credential under the right account (the ART-7 ruling).
 */
export function JoinScreen({ token, onJoined, onSignIn, onLeave }: JoinScreenProps) {
  const auth = useAuth();
  const [signedIn, setSignedIn] = useState(() => auth.isSignedIn());
  const preview = useInvitePreview(token);

  if (!token || preview.data === null || (preview.data && preview.data.state !== "open")) {
    const accepted = preview.data?.state === "accepted";
    return (
      <AuthCard
        title={accepted ? "Invite already accepted" : "This invite link isn't valid"}
        subtitle={
          accepted
            ? "This invite has already been used. Sign in to get to your account."
            : "It may have expired or been replaced by a newer one. Ask the person who invited you to send it again."
        }
        footer={
          <Button variant="link" type="button" onClick={onLeave}>
            Go to sign in
          </Button>
        }
      >
        <span />
      </AuthCard>
    );
  }

  if (preview.isLoading || !preview.data) {
    return (
      <AuthCard title="Opening your invite…">
        {preview.isError ? (
          <p className={styles.serverError} role="alert">
            Couldn't open this invite. Check your connection and try again.
          </p>
        ) : (
          <div role="status" aria-live="polite">
            <Spinner />
          </div>
        )}
      </AuthCard>
    );
  }

  const p = preview.data;
  const { title, subtitle } = describe(p);

  if (p.isArtistAccount) {
    return (
      <AuthCard
        title={title}
        subtitle={`This invite was sent to ${p.email}, which is an artist account. Artist accounts can't join another band or team — ask for the invite to go to your personal address.`}
        footer={
          <Button variant="link" type="button" onClick={onLeave}>
            Go to sign in
          </Button>
        }
      >
        <span />
      </AuthCard>
    );
  }

  if (signedIn) {
    return (
      <SignedInAccept
        preview={p}
        title={title}
        subtitle={subtitle}
        onJoined={onJoined}
        onSignedOut={() => setSignedIn(false)}
      />
    );
  }

  if (p.accountExists) {
    return (
      <AuthCard
        title={title}
        subtitle={<>{subtitle} You already have an account as <b>{p.email}</b> — sign in to accept.</>}
      >
        <Button variant="accent" type="button" fullWidth onClick={onSignIn}>
          Sign in to accept
        </Button>
      </AuthCard>
    );
  }

  return <CreateAccountForm token={token} preview={p} title={title} subtitle={subtitle} onJoined={onJoined} />;
}

function CreateAccountForm({
  token,
  preview,
  title,
  subtitle,
  onJoined,
}: {
  token: string;
  preview: InvitePreview;
  title: string;
  subtitle: string;
  onJoined: () => void;
}) {
  const auth = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<JoinValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: { firstName: preview.firstName ?? "", lastName: preview.lastName ?? "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await auth.joinByInvite({
        token,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        password: values.password,
      });
      onJoined();
    } catch (err) {
      setServerError(
        err instanceof AuthError
          ? err.message
          : "Something went wrong on this device — check your connection and try again.",
      );
    }
  });

  return (
    <AuthCard title={title} subtitle={`${subtitle} Set up your account to accept.`}>
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        <Field label="Email" type="email" autoComplete="username" value={preview.email ?? ""} readOnly />
        <Field
          label="First name"
          autoComplete="given-name"
          error={errors.firstName?.message}
          {...register("firstName")}
        />
        <Field
          label="Last name"
          autoComplete="family-name"
          error={errors.lastName?.message}
          {...register("lastName")}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••••••"
          error={errors.password?.message}
          {...register("password")}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          placeholder="••••••••••••"
          error={errors.confirm?.message}
          {...register("confirm")}
        />
        {serverError && (
          <p className={styles.serverError} role="alert">
            {serverError}
          </p>
        )}
        <Button variant="accent" type="submit" fullWidth loading={isSubmitting}>
          {preview.kind === "partner" ? "Create account" : "Create account and join"}
        </Button>
      </form>
    </AuthCard>
  );
}

function SignedInAccept({
  preview,
  title,
  subtitle,
  onJoined,
  onSignedOut,
}: {
  preview: InvitePreview;
  title: string;
  subtitle: string;
  onJoined: () => void;
  onSignedOut: () => void;
}) {
  const auth = useAuth();
  const me = useMe();
  const accept = useAcceptInvite();
  const [error, setError] = useState<string | null>(null);
  const myEmail = me.data?.person?.email;

  if (me.isLoading) {
    return (
      <AuthCard title={title}>
        <div role="status" aria-live="polite">
          <Spinner />
        </div>
      </AuthCard>
    );
  }

  // Signed in as somebody else: say so, and never accept for the wrong person (the server would refuse anyway).
  if (myEmail && myEmail.toLowerCase() !== (preview.email ?? "").toLowerCase()) {
    return (
      <AuthCard
        title={title}
        subtitle={<>This invite was sent to <b>{preview.email}</b>, but you're signed in as <b>{myEmail}</b>.</>}
      >
        <Button
          variant="accent"
          type="button"
          fullWidth
          onClick={async () => {
            await auth.logout();
            onSignedOut();
          }}
        >
          Sign out and continue
        </Button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={title} subtitle={subtitle}>
      {error && (
        <p className={styles.serverError} role="alert">
          {error}
        </p>
      )}
      <Button
        variant="accent"
        type="button"
        fullWidth
        loading={accept.isPending}
        onClick={() =>
          accept.mutate(preview.inviteId ?? "", {
            onSuccess: onJoined,
            onError: (err) => setError(err instanceof Error ? err.message : "Try again."),
          })
        }
      >
        Accept invite
      </Button>
    </AuthCard>
  );
}
